import { useState } from "react";

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus, Trash2, ListOrdered, Pencil, ArrowUp, ArrowDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { PageHeader } from "@/components/PageHeader";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/auth-context";
import { api } from "@/lib/api";

interface SalesStage {
  id: string;
  name: string;
  displayOrder: number;
  defaultProbability: number;
  color: string;
  isActive: boolean;
}

const COLORS = ["gray", "purple", "blue", "amber", "green", "red", "sky", "emerald"];

export default function SalesStagesSettingsPage() {
  const { sessionId } = useAuth();

  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({ name: "", defaultProbability: 0, color: "gray", isActive: true });

  const { data: stages = [] } = useQuery<SalesStage[]>({
    queryKey: ["/api/sales-stages", { includeInactive: true }],
    queryFn: () => api.get<SalesStage[]>(`/sales-stages?includeInactive=true`, sessionId),
  });

  const saveMutation = useMutation({
    mutationFn: () => editingId
      ? api.patch(`/sales-stages/${editingId}`, form, sessionId)
      : api.post(`/sales-stages`, form, sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/sales-stages"] });
      setDialogOpen(false);
      setEditingId(null);
      toast({ title: editingId ? "Stage updated" : "Stage created" });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/sales-stages/${id}`, sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/sales-stages"] });
      toast({ title: "Stage deleted" });
    },
  });

  const reorderMutation = useMutation({
    mutationFn: (stageIds: string[]) => api.post(`/sales-stages/reorder`, { stageIds }, sessionId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/sales-stages"] }),
  });

  const handleEdit = (s: SalesStage) => {
    setEditingId(s.id);
    setForm({
      name: s.name, defaultProbability: s.defaultProbability,
      color: s.color || "gray", isActive: s.isActive,
    });
    setDialogOpen(true);
  };

  const handleNew = () => {
    setEditingId(null);
    setForm({ name: "", defaultProbability: 0, color: "gray", isActive: true });
    setDialogOpen(true);
  };

  const handleDelete = (s: SalesStage) => {
    if (confirm(`Delete sales stage "${s.name}"?`)) {
      deleteMutation.mutate(s.id);
    }
  };

  const handleMove = (index: number, dir: "up" | "down") => {
    const newIndex = dir === "up" ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= stages.length) return;
    const newOrder = [...stages];
    [newOrder[index], newOrder[newIndex]] = [newOrder[newIndex], newOrder[index]];
    reorderMutation.mutate(newOrder.map(s => s.id));
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Sales Stages"
        description="Configure the sales pipeline stages used by opportunities"
        actions={
          <div className="flex items-center gap-2">
            <Button onClick={handleNew} data-testid="button-new-stage">
              <Plus className="h-4 w-4 mr-1" /> Add Stage
            </Button>
          </div>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <ListOrdered className="h-4 w-4" /> All Stages ({stages.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {stages.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">No sales stages configured.</p>
          ) : (
            <div className="space-y-2">
              {stages.map((s, idx) => (
                <div
                  key={s.id}
                  className="flex items-center justify-between py-2 px-3 rounded-md hover:bg-muted/40"
                >
                  <div className="flex items-center gap-3 flex-1">
                    <div className="flex flex-col">
                      <Button
                        variant="ghost" size="icon" className="h-5 w-5"
                        disabled={idx === 0}
                        onClick={() => handleMove(idx, "up")}
                      >
                        <ArrowUp className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="ghost" size="icon" className="h-5 w-5"
                        disabled={idx === stages.length - 1}
                        onClick={() => handleMove(idx, "down")}
                      >
                        <ArrowDown className="h-3 w-3" />
                      </Button>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs text-muted-foreground">#{s.displayOrder}</span>
                        <span className="font-medium text-sm">{s.name}</span>
                        <Badge variant="outline" className="text-[10px]">{s.color}</Badge>
                        <Badge className="text-[10px]">{s.defaultProbability}%</Badge>
                        {!s.isActive && <Badge variant="secondary" className="text-[10px]">Inactive</Badge>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEdit(s)}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost" size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-destructive"
                      onClick={() => handleDelete(s)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit Sales Stage" : "New Sales Stage"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Name *</label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Qualification"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium mb-1 block">Default Probability %</label>
                <Input
                  type="number" min={0} max={100}
                  value={form.defaultProbability}
                  onChange={(e) => setForm({ ...form, defaultProbability: Math.min(100, Math.max(0, Number(e.target.value) || 0)) })}
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Color</label>
                <select
                  className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                  value={form.color}
                  onChange={(e) => setForm({ ...form, color: e.target.value })}
                >
                  {COLORS.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
              />
              Active
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button
                onClick={() => saveMutation.mutate()}
                disabled={!form.name || saveMutation.isPending}
              >
                {saveMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingId ? "Save Changes" : "Create"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
